// src/pages/UserDashboard/UserDashboard.jsx
import React, { useEffect, useState, useContext, useRef, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { FaDiscord, FaSignOutAlt, FaCheckCircle, FaClock, FaTimesCircle, FaClipboardList, FaExternalLinkAlt, FaShieldAlt, FaUserEdit, FaUser, FaWrench, FaChevronDown, FaDollarSign, FaCar, FaImage, FaTrashAlt, FaCamera, FaBoxOpen, FaExchangeAlt, FaHeartbeat, FaIdCard, FaChartLine, FaGasPump, FaStar, FaEnvelope } from 'react-icons/fa';
import bgImg from '../../assets/night-city-lights-city-grand-theft-auto-v-gta5-hd-wallpaper-a8b66d38a030dc28101c81def822440a.jpg';
import './UserDashboard.css';

const UserDashboard = () => {
  const { user, logout, authToken, validateToken } = useContext(AuthContext);
  const apiBase = process.env.REACT_APP_API_BASE_URL || 'https://riasbaixaspd.es/api';
  const [whitelistData, setWhitelistData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(user ? 'resumen' : 'galeria');
  const navigate = useNavigate();
  const location = useLocation();

  // Estados para datos de FiveM QBCore (Opción A)
  const [fivemProfile, setFivemProfile] = useState(null);
  const [fivemProfileLoading, setFivemProfileLoading] = useState(false);
  const [fivemProfileError, setFivemProfileError] = useState('');
  const [selectedCharIndex, setSelectedCharIndex] = useState(0);
  const [charSubTab, setCharSubTab] = useState('perfil');

  // Estados para transferencias bancarias
  const [transferRecipient, setTransferRecipient] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [transferSuccess, setTransferSuccess] = useState('');
  const [transferError, setTransferError] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferRecipientName, setTransferRecipientName] = useState('');
  const [checkingRecipient, setCheckingRecipient] = useState(false);
  const [recipientVerified, setRecipientVerified] = useState(null); // null = not checked, true = verified, false = failed
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Estados para el formulario de Solicitud de Cambio
  const [changeType, setChangeType] = useState('Datos de Whitelist');
  const [changeMessage, setChangeMessage] = useState('');
  const [changeEmail, setChangeEmail] = useState('');
  const [changeSuccess, setChangeSuccess] = useState('');
  const [changeError, setChangeError] = useState('');
  const [submittingChange, setSubmittingChange] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');

  // Estado para el modal de edición de perfil
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editUsername, setEditUsername] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editMsg, setEditMsg] = useState('');

  // Estados para el sistema de galería
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryError, setGalleryError] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadComment, setUploadComment] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [uploadError, setUploadError] = useState('');

  // Ref para evitar polling infinito en el perfil de FiveM
  const hasFetchedProfile = useRef(false);

  // Estado y ref para desplegable personalizado
  const dropdownRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Estados para el sistema de valoraciones
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState('');
  const [reviewsStats, setReviewsStats] = useState({ average: null, total: 0, distribution: [] });
  const [myRating, setMyRating] = useState(0);
  const [myRatingHover, setMyRatingHover] = useState(0);
  const [myComment, setMyComment] = useState('');
  const [submitReviewLoading, setSubmitReviewLoading] = useState(false);
  const [submitReviewSuccess, setSubmitReviewSuccess] = useState('');
  const [submitReviewError, setSubmitReviewError] = useState('');
  const [hasAlreadyReviewed, setHasAlreadyReviewed] = useState(false);
  const [activePhoto, setActivePhoto] = useState(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [location.search]);

  // Hook para buscar destinatario en tiempo real
  useEffect(() => {
    if (transferRecipient.length !== 8) {
      setTransferRecipientName('');
      setCheckingRecipient(false);
      setRecipientVerified(null);
      return;
    }

    const checkRecipient = async () => {
      setCheckingRecipient(true);
      setRecipientVerified(null);
      setTransferRecipientName('');
      try {
        const response = await fetch(`${apiBase}/fivem/charname/${transferRecipient}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });
        const data = await response.json();
        if (response.ok && data.found) {
          setTransferRecipientName(data.name);
          setRecipientVerified(true);
        } else {
          setRecipientVerified(false);
          setTransferRecipientName('');
        }
      } catch (err) {
        console.error('Error al verificar destinatario:', err);
        setRecipientVerified(false);
        setTransferRecipientName('');
      } finally {
        setCheckingRecipient(false);
      }
    };

    const timer = setTimeout(() => {
      checkRecipient();
    }, 250);

    return () => clearTimeout(timer);
  }, [transferRecipient, apiBase, authToken]);

  const handleRequestVerification = async () => {
    setVerificationMessage('Verificando...');
    try {
      const response = await fetch(`${apiBase}/auth/verify-discord`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        setVerificationMessage('¡Verificado con éxito!');
        await validateToken();
      } else {
        setVerificationMessage(data.message || 'Error al verificar.');
      }
    } catch (err) {
      setVerificationMessage('Error de red al intentar verificar.');
    }
  };

  const fetchFiveMProfile = useCallback(async (force = false) => {
    if (!user?.discord_id) return;
    if (hasFetchedProfile.current && !force) return; // Evitar llamadas duplicadas
    hasFetchedProfile.current = true;
    setFivemProfileLoading(true);
    setFivemProfileError('');
    try {
      const res = await fetch(`${apiBase}/fivem/profile`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      const data = await res.json();
      if (res.ok) {
        if (data.found) {
          setFivemProfile(data);
        } else {
          setFivemProfile(data); // Contiene serverIp y serverPort
        }
      } else {
        setFivemProfile(data); // Contiene serverIp y serverPort
        setFivemProfileError(data.message || 'No se pudo conectar con el servidor de juegos.');
      }
    } catch (err) {
      console.error('Error fetching FiveM profile:', err);
      setFivemProfileError('No se pudo establecer conexión con los datos del personaje.');
    } finally {
      setFivemProfileLoading(false);
    }
  }, [user?.discord_id, authToken, apiBase]);

  const handleBankTransfer = (e) => {
    e.preventDefault();
    if (!transferRecipient || !transferAmount) {
      setTransferError('Por favor, completa todos los campos.');
      return;
    }
    const amountNum = parseInt(transferAmount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setTransferError('Cantidad no válida.');
      return;
    }

    const hasCharacters = fivemProfile && fivemProfile.characters && fivemProfile.characters.length > 0;
    const activeChar = hasCharacters ? (fivemProfile.characters[selectedCharIndex] || fivemProfile.characters[0]) : null;
    if (!activeChar) {
      setTransferError('No hay ningún personaje seleccionado.');
      return;
    }

    if (amountNum > (activeChar.money?.bank || 0)) {
      setTransferError('Saldo insuficiente en tu cuenta bancaria.');
      return;
    }

    if (transferRecipient === activeChar.citizenid) {
      setTransferError('No puedes transferir dinero a ti mismo.');
      return;
    }

    if (!recipientVerified || !transferRecipientName) {
      setTransferError('El destinatario no ha sido verificado o no existe.');
      return;
    }

    setTransferError('');
    setTransferSuccess('');
    setShowConfirmModal(true);
  };

  const executeBankTransfer = async () => {
    const hasCharacters = fivemProfile && fivemProfile.characters && fivemProfile.characters.length > 0;
    const activeChar = hasCharacters ? (fivemProfile.characters[selectedCharIndex] || fivemProfile.characters[0]) : null;
    if (!activeChar) return;

    setTransferLoading(true);
    setTransferError('');
    setTransferSuccess('');
    setShowConfirmModal(false);

    try {
      const res = await fetch(`${apiBase}/fivem/transfer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          sender_citizenid: activeChar.citizenid,
          recipient_citizenid: transferRecipient,
          amount: parseInt(transferAmount)
        })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setTransferSuccess(data.message || 'Transferencia realizada con éxito.');
        setTransferRecipient('');
        setTransferAmount('');
        fetchFiveMProfile(true);
      } else {
        setTransferError(data.message || 'La transferencia ha fallado.');
      }
    } catch (err) {
      console.error('Error executing transfer:', err);
      setTransferError('Error de red al intentar realizar la transferencia.');
    } finally {
      setTransferLoading(false);
    }
  };

  const fetchGallery = useCallback(async () => {
    setGalleryLoading(true);
    setGalleryError('');
    try {
      const response = await fetch(`${apiBase}/gallery`);
      if (!response.ok) {
        throw new Error('No se pudo cargar la galería de fotos');
      }
      const data = await response.json();
      setGalleryPhotos(data);
    } catch (err) {
      console.error(err);
      setGalleryError('Error al cargar las fotos de la galería.');
    } finally {
      setGalleryLoading(false);
    }
  }, [apiBase]);

  const fetchReviews = useCallback(async () => {
    setReviewsLoading(true);
    setReviewsError('');
    try {
      const response = await fetch(`${apiBase}/reviews`);
      if (!response.ok) throw new Error('Error al cargar valoraciones');
      const data = await response.json();
      setReviews(data.reviews || []);
      setReviewsStats({ average: data.average, total: data.total, distribution: data.distribution || [] });
      // Comprobar si el usuario ya valoró
      if (user?.discord_id) {
        const alreadyDone = (data.reviews || []).some(r => r.discord_id === user.discord_id);
        setHasAlreadyReviewed(alreadyDone);
      }
    } catch (err) {
      setReviewsError('No se pudieron cargar las valoraciones.');
    } finally {
      setReviewsLoading(false);
    }
  }, [apiBase, user?.discord_id]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!myRating) { setSubmitReviewError('Selecciona una puntuación.'); return; }
    setSubmitReviewLoading(true);
    setSubmitReviewError('');
    setSubmitReviewSuccess('');
    try {
      const res = await fetch(`${apiBase}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ rating: myRating, comment: myComment.trim() })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSubmitReviewSuccess(data.message || '¡Gracias por tu valoración!');
        setHasAlreadyReviewed(true);
        setMyRating(0);
        setMyComment('');
        fetchReviews();
      } else {
        setSubmitReviewError(data.message || 'Error al enviar la valoración.');
      }
    } catch (err) {
      setSubmitReviewError('Error de red al enviar tu valoración.');
    } finally {
      setSubmitReviewLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      setUploadError('Por favor, selecciona una foto para subir.');
      return;
    }
    setUploading(true);
    setUploadError('');
    setUploadSuccess('');

    const formData = new FormData();
    formData.append('photo', uploadFile);
    formData.append('comment', uploadComment);

    try {
      const response = await fetch(`${apiBase}/gallery/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`
        },
        body: formData
      });
      const data = await response.json();
      if (response.ok) {
        setUploadSuccess('¡Foto subida con éxito a la galería!');
        setUploadComment('');
        setUploadFile(null);
        fetchGallery();
      } else {
        setUploadError(data.message || 'Error al subir la foto.');
      }
    } catch (err) {
      console.error(err);
      setUploadError('Error de red al intentar subir la foto.');
    } finally {
      setUploading(false);
    }
  };

  const handlePhotoDelete = async (photoId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta foto de la galería?')) return;
    try {
      const response = await fetch(`${apiBase}/gallery/${photoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        fetchGallery();
      } else {
        alert(data.message || 'Error al borrar la foto.');
      }
    } catch (err) {
      console.error(err);
      alert('Error de red al intentar borrar la foto.');
    }
  };

  useEffect(() => {
    if (!user) {
      fetchGallery();
      fetchReviews();
      setLoading(false);
      return;
    }

    // Refrescar estado del usuario y rol de Discord al cargar el panel
    validateToken().catch(err => {
      console.warn('No se pudo validar/refrescar el token del usuario:', err.message);
    });

    // Solo buscar whitelist si el usuario se autenticó con Discord (tiene discord_id)
    const isDiscordUser = !!user.discord_id;
    if (!isDiscordUser) {
      // Usuario staff sin Discord — no hay whitelist que mostrar
      setLoading(false);
      return;
    }

    const fetchMyWhitelist = async () => {
      try {
        const response = await fetch(`${apiBase}/whitelistRequests/me`, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });

        if (!response.ok) {
          throw new Error('Error al obtener los datos de la whitelist');
        }

        const data = await response.json();
        if (data.found) {
          setWhitelistData(data.request);
        } else {
          setWhitelistData(null);
        }
      } catch (err) {
        console.error(err);
        setError('No se pudo cargar la información de tu whitelist.');
      } finally {
        setLoading(false);
      }
    };

    fetchMyWhitelist();
    fetchFiveMProfile();
    fetchGallery();
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, authToken, fetchFiveMProfile, fetchGallery, fetchReviews]);

  const handleLogout = () => {
    logout();
  };

  const handleRetry = async () => {
    if (!whitelistData || !whitelistData.id) return;
    setLoading(true);
    try {
      const response = await fetch(`${apiBase}/whitelistRequests/${whitelistData.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      if (response.ok) {
        setWhitelistData(null);
        setError('');
      } else {
        const errData = await response.json();
        setError(`Error al reiniciar: ${errData.message || 'Inténtalo de nuevo.'}`);
      }
    } catch (err) {
      setError('Error al reiniciar la solicitud.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeRequestSubmit = async (e) => {
    e.preventDefault();
    setChangeSuccess('');
    setChangeError('');
    setSubmittingChange(true);
    try {
      const response = await fetch(`${apiBase}/whitelistRequests/change-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          type: changeType,
          message: changeMessage,
          email: changeEmail
        })
      });
      if (response.ok) {
        setChangeSuccess('Solicitud de cambio enviada correctamente. El Staff la revisará pronto.');
        setChangeMessage('');
        setChangeEmail('');
      } else {
        const errData = await response.json();
        setChangeError(errData.message || 'Error al enviar la solicitud de cambio.');
      }
    } catch (err) {
      setChangeError('Error de red al enviar la solicitud.');
    } finally {
      setSubmittingChange(false);
    }
  };

  const pageStyle = {
    backgroundImage: `url(${bgImg})`,
  };

  // Obtener URL de avatar de Discord
  const getAvatarUrl = () => {
    const discordUserId = user?.discord_id || user?.id;
    if (discordUserId && user?.avatar) {
      return `https://cdn.discordapp.com/avatars/${discordUserId}/${user.avatar}.png?size=256`;
    }
    // Avatar por defecto de Discord
    return 'https://cdn.discordapp.com/embed/avatars/0.png';
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved': return 'Aprobado';
      case 'pending': return 'Pendiente de Revisión';
      case 'rejected': return 'Suspendida';
      default: return 'No Enviado';
    }
  };

  const getStatusClass = (status) => {
    if (!status) return 'status-none';
    return `status-${status}`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <FaCheckCircle style={{ color: '#00e676' }} />;
      case 'pending': return <FaClock style={{ color: '#ff9100' }} />;
      case 'rejected': return <FaTimesCircle style={{ color: '#ff1744' }} />;
      default: return <FaClipboardList style={{ color: '#1ca7ec' }} />;
    }
  };

  if (loading) {
    return (
      <div className="dashboard-page" style={pageStyle}>
        <div className="dashboard-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
          <div className="modern-spinner"></div>
        </div>
      </div>
    );
  }

  const whitelistStatus = whitelistData ? whitelistData.status : 'none';
  const hasCharacters = fivemProfile && fivemProfile.characters && fivemProfile.characters.length > 0;
  const activeChar = hasCharacters ? (fivemProfile.characters[selectedCharIndex] || fivemProfile.characters[0]) : null;

  return (
    <div className="dashboard-page" style={pageStyle}>
      <div className="dashboard-container">

        {/* Sidebar del Perfil */}
        {user ? (
          <aside className="dashboard-sidebar glass-panel">
            <div className="user-avatar-container">
              <img src={getAvatarUrl()} alt="Avatar de Discord" className="user-avatar-img" />
              <div className="discord-badge">
                <FaDiscord />
              </div>
            </div>

            <h3 className="user-profile-name">{user?.username || 'Usuario'}</h3>
            {user?.discord_id ? (
              <p className="user-profile-id">Discord ID: {user.discord_id}</p>
            ) : (
              <p className="user-profile-id" style={{ color: 'rgba(255,215,0,0.7)' }}>Cuenta de Staff</p>
            )}


            {/* Verificación Discord: solo relevante para usuarios normales */}
            {user?.discord_id ? (
              user?.hasMemberRole ? (
                <div className="verification-badge verified">
                  <FaCheckCircle />
                  <span>Verificado</span>
                </div>
              ) : (
                <>
                  <div className="verification-badge unverified">
                    <FaTimesCircle />
                    <span>No Verificado</span>
                  </div>
                  <button
                    onClick={handleRequestVerification}
                    className="verify-discord-btn"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: 'linear-gradient(90deg, #5865f2 0%, #7289da 100%)',
                      color: '#fff',
                      border: 'none',
                      padding: '10px 15px',
                      borderRadius: '12px',
                      fontSize: '0.85rem',
                      fontWeight: '700',
                      cursor: 'pointer',
                      marginBottom: '15px',
                      width: '100%',
                      justifyContent: 'center',
                      boxShadow: '0 4px 10px rgba(88, 101, 242, 0.2)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <span>Verificar Discord</span>
                  </button>
                  {verificationMessage && (
                    <p style={{ fontSize: '0.8rem', color: '#ff9100', marginTop: '-8px', marginBottom: '15px', fontWeight: '500' }}>
                      {verificationMessage}
                    </p>
                  )}
                  <p className="verification-tip">
                    Obtén el rol en nuestro <a href="https://discord.gg/riasbaix" target="_blank" rel="noopener noreferrer">Discord</a> para estar verificado.
                  </p>
                </>
              )
            ) : (
              <div className="verification-badge verified" style={{ background: 'rgba(255,215,0,0.1)', color: '#ffd700', border: '1px solid rgba(255,215,0,0.3)' }}>
                <FaShieldAlt />
                <span>Staff</span>
              </div>
            )}

            <div className="dashboard-menu">
              {/* Botón de editar perfil */}
              <button
                onClick={() => { 
                  setEditUsername(user?.username || ''); 
                  setEditEmail(user?.email || ''); 
                  setEditMsg(''); 
                  setShowEditProfile(true); 
                }}
                className="edit-profile-btn"
              >
                <FaUserEdit />
                <span>Editar Perfil</span>
              </button>

              {/* Acceso al panel de administración (solo staff) */}
              {user && (user.role === 'superadmin' || (Array.isArray(user.permissions) && user.permissions.includes('ver_dashboard'))) && (
                <Link to="/admin" className="admin-panel-btn">
                  <FaShieldAlt />
                  <span>Panel de Administración</span>
                </Link>
              )}

              <button onClick={handleLogout} className="logout-btn">
                <FaSignOutAlt />
                <span>Cerrar Sesión</span>
              </button>
            </div>
          </aside>
        ) : (
          <aside className="dashboard-sidebar glass-panel">
            <div className="user-avatar-container">
              <div style={{
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                border: '3px solid rgba(255, 255, 255, 0.1)',
                background: 'rgba(0, 0, 0, 0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '3rem',
                color: 'rgba(255, 255, 255, 0.15)',
                boxShadow: '0 0 15px rgba(255, 255, 255, 0.05)'
              }}>
                <FaUser />
              </div>
            </div>

            <h3 className="user-profile-name">Invitado</h3>
            <p className="user-profile-id" style={{ marginBottom: '20px' }}>Visualizando Galería Pública</p>

            <div className="dashboard-menu" style={{ width: '100%' }}>
              <Link
                to="/login"
                className="dashboard-action-btn primary"
                style={{
                  width: '100%',
                  justifyContent: 'center',
                  boxSizing: 'border-box',
                  display: 'flex',
                  padding: '12px 15px',
                  borderRadius: '10px',
                  fontWeight: '700'
                }}
              >
                <FaDiscord /> <span>Iniciar Sesión</span>
              </Link>
            </div>
          </aside>
        )}

        {/* Panel Principal */}
        <main className="dashboard-content-panel glass-panel">
          {user && !user.discord_id ? (
            // Vista para usuarios Staff (sin Discord) 
            <div style={{ padding: '10px 0' }}>
              <div className="panel-header">
                <h2 className="panel-title">Mi Perfil de Staff</h2>
              </div>
              <div className="status-section status-approved" style={{ marginBottom: 30 }}>
                <div className="status-icon-container"><FaShieldAlt style={{ color: '#ffd700' }} /></div>
                <div className="status-details">
                  <h3 style={{ color: '#ffd700' }}>Cuenta de Administración</h3>
                  <p>Tienes acceso al panel de administración. Usa el botón del sidebar para acceder.</p>
                </div>
              </div>
              <div className="whitelist-details-card">
                <h4>Información de cuenta</h4>
                <div className="detail-row">
                  <div className="detail-row-label">Usuario</div>
                  <div className="detail-row-value">{user?.username}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-row-label">Rol</div>
                  <div className="detail-row-value" style={{ color: '#ffd700', textTransform: 'capitalize' }}>{user?.role}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-row-label">Discord ID vinculado</div>
                  <div className="detail-row-value" style={{ color: user?.discord_id ? '#00e676' : '#ff9100' }}>
                    {user?.discord_id || 'No vinculado — ve a Gestión de Usuarios para vincularlo'}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Vista para usuarios normales (con Discord)
            <>
              {/* Tab Navigation Menu */}
              {new URLSearchParams(location.search).get('from') !== 'inicio' && (
                <div className="dashboard-tabs">
                  {user && (
                    <>
                      <button
                        className={`tab-btn ${activeTab === 'resumen' ? 'active' : ''}`}
                        onClick={() => setActiveTab('resumen')}
                      >
                        <FaClipboardList /> <span>Resumen Whitelist</span>
                      </button>
                      <button
                        className={`tab-btn ${activeTab === 'personaje' ? 'active' : ''}`}
                        onClick={() => {
                          setActiveTab('personaje');
                          fetchFiveMProfile(true);
                        }}
                      >
                        <FaUser /> <span>Mi Personaje</span>
                      </button>
                      <button
                        className={`tab-btn ${activeTab === 'soporte' ? 'active' : ''}`}
                        onClick={() => setActiveTab('soporte')}
                      >
                        <FaWrench /> <span>Solicitudes</span>
                      </button>
                    </>
                  )}
                  <button
                    className={`tab-btn ${activeTab === 'galeria' ? 'active' : ''}`}
                    onClick={() => {
                      setActiveTab('galeria');
                      fetchGallery();
                    }}
                  >
                    <FaImage /> <span>Galería</span>
                  </button>
                  <button
                    className={`tab-btn ${activeTab === 'valoraciones' ? 'active' : ''}`}
                    onClick={() => {
                      setActiveTab('valoraciones');
                      fetchReviews();
                    }}
                  >
                    <FaStar /> <span>Valoraciones</span>
                  </button>
                </div>
              )}

              {error && <div className="error-message">{error}</div>}

              {/* TAB 1: RESUMEN / ESTADO */}
              {activeTab === 'resumen' && (
                <div className="tab-pane-content">
                  <div className="panel-header">
                    <h2 className="panel-title">Estado de tu Whitelist</h2>
                  </div>

                  {/* Tarjeta de Estado */}
                  <div className={`status-section ${getStatusClass(whitelistStatus)}`}>
                    <div className="status-icon-container">
                      {getStatusIcon(whitelistStatus)}
                    </div>
                    <div className="status-details">
                      <h3>{getStatusText(whitelistStatus)}</h3>
                      {whitelistStatus === 'approved' && (
                        <p>¡Felicidades! Tu whitelist ha sido aprobada. Ya puedes entrar a jugar.</p>
                      )}
                      {whitelistStatus === 'pending' && (
                        <p>Tu solicitud está siendo evaluada por el Staff. Te notificaremos pronto.</p>
                      )}
                      {whitelistStatus === 'rejected' && (
                        <div>
                          <p style={{ marginBottom: '15px' }}>Tu solicitud ha sido rechazada o suspendida. Revisa los detalles abajo o vuelve a intentarlo.</p>
                          <button onClick={handleRetry} className="dashboard-action-btn primary" style={{ cursor: 'pointer', border: 'none' }}>
                            Volver a Intentarlo
                          </button>
                        </div>
                      )}
                      {whitelistStatus === 'none' && (
                        <p>Aún no has enviado tu solicitud para ingresar al servidor.</p>
                      )}
                    </div>
                  </div>

                  {/* Secciones Dinámicas según estado */}
                  {whitelistStatus === 'none' && (
                    <div className="whitelist-submission-info">
                      <p style={{ color: '#bbb', lineHeight: '1.6', marginBottom: 25 }}>
                        Para poder jugar en nuestro servidor de roleplay de FiveM, es obligatorio que completes y envíes el formulario de whitelist. El staff lo revisará lo antes posible.
                      </p>
                      <div className="action-btn-container">
                        <Link to="/whitelist" className="dashboard-action-btn primary">
                          <FaClipboardList />
                          <span>Enviar Formulario</span>
                        </Link>
                        <a href="https://discord.gg/riasbaix" target="_blank" rel="noopener noreferrer" className="dashboard-action-btn secondary">
                          <span>Unirse al Discord</span>
                          <FaExternalLinkAlt size={12} />
                        </a>
                      </div>
                    </div>
                  )}

                  {whitelistStatus === 'approved' && (
                    <div className="whitelist-submission-info">
                      <h4 style={{ color: '#fff', marginBottom: 15 }}>Instrucciones de Conexión</h4>
                      <p style={{ color: '#bbb', lineHeight: '1.6', marginBottom: 25 }}>
                        Ya puedes conectarte al servidor. Haz clic en el botón de abajo para ver la guía detallada de conexión o conectarte directamente.
                      </p>
                      <div className="action-btn-container">
                        <Link to="/como-conectarse" className="dashboard-action-btn primary">
                          <span>Guía de Conexión</span>
                        </Link>
                      </div>
                    </div>
                  )}

                  {whitelistData && (
                    <div className="whitelist-details-card">
                      <h4>Detalles de tu solicitud</h4>

                      <div className="detail-row">
                        <div className="detail-row-label">Nombre del Personaje (FiveM)</div>
                        <div className="detail-row-value">{whitelistData.username}</div>
                      </div>

                      <div className="detail-row">
                        <div className="detail-row-label">Edad OOC</div>
                        <div className="detail-row-value">{whitelistData.ooc_edad}</div>
                      </div>

                      {whitelistData.ooc_experiencia_rol && whitelistData.ooc_experiencia_rol !== 'N/D' && (
                        <div className="detail-row">
                          <div className="detail-row-label">Experiencia en Roleplay</div>
                          <div className="detail-row-value">{whitelistData.ooc_experiencia_rol}</div>
                        </div>
                      )}

                      {whitelistData.ic_historia_pj && whitelistData.ic_historia_pj !== 'N/D' && (
                        <div className="detail-row">
                          <div className="detail-row-label">Historia de tu personaje</div>
                          <div className="detail-row-value">{whitelistData.ic_historia_pj}</div>
                        </div>
                      )}

                      {whitelistData.ic_futuro_pj && whitelistData.ic_futuro_pj !== 'N/D' && (
                        <div className="detail-row">
                          <div className="detail-row-label">Objetivos del personaje</div>
                          <div className="detail-row-value">{whitelistData.ic_futuro_pj}</div>
                        </div>
                      )}

                      {whitelistData.ic_aptitudes_pj && whitelistData.ic_aptitudes_pj !== 'N/D' && (
                        <div className="detail-row">
                          <div className="detail-row-label">Aptitudes del personaje</div>
                          <div className="detail-row-value">{whitelistData.ic_aptitudes_pj}</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* TAB 2: MI PERSONAJE */}
              {activeTab === 'personaje' && (
                <div className="tab-pane-content">
                  <div className="panel-header">
                    <h2 className="panel-title">Mi Personaje en RiasBaixasPD</h2>
                  </div>

                  <div className="fivem-character-section" style={{ marginTop: '10px' }}>
                    {fivemProfileLoading ? (
                      <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
                        <div className="modern-spinner" style={{ width: '30px', height: '30px' }}></div>
                      </div>
                    ) : fivemProfileError ? (
                      <div style={{ textAlign: 'center', padding: '10px 0' }}>
                        <div className="error-message" style={{ padding: '12px 20px', borderRadius: '10px', display: 'inline-block', marginBottom: '20px', background: 'rgba(255,23,72,0.06)', border: '1px solid rgba(255,23,72,0.2)', color: '#ff1744', fontSize: '0.9rem' }}>
                          ⚠️ {fivemProfileError}
                        </div>
                        <div style={{ background: 'rgba(255,255,255,0.02)', padding: '24px 20px', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.05)', maxWidth: '500px', margin: '0 auto', textAlign: 'center' }}>
                          <p style={{ color: '#ccc', fontSize: '0.95rem', margin: '0 0 8px 0', fontWeight: 600 }}>
                            Estado del Servidor de Juegos
                          </p>
                          <p style={{ color: '#aaa', fontSize: '0.85rem', margin: '0 0 14px 0', lineHeight: 1.6 }}>
                            Asegúrate de que tu servidor FiveM local está **encendido** y que el recurso de conexión <code style={{ color: '#1ca7ec' }}>mestr-api</code> está iniciado en el archivo de configuración <code style={{ color: '#1ca7ec' }}>server.cfg</code>.
                          </p>
                          <p style={{ color: '#888', fontSize: '0.8rem', margin: '0 0 8px 0' }}>
                            Dirección de conexión configurada:
                          </p>
                          <code style={{ background: 'rgba(0,0,0,0.3)', padding: '8px 16px', borderRadius: '6px', color: '#1ca7ec', fontSize: '0.9rem', display: 'inline-block', fontFamily: 'monospace', fontWeight: 'bold' }}>
                            connect {fivemProfile?.serverIp || '127.0.0.1'}:{fivemProfile?.serverPort || '30120'}
                          </code>
                        </div>
                      </div>
                    ) : hasCharacters && activeChar ? (
                      <>
                        {/* Selector de PJs (Tabs cristalinas premium) */}
                        {fivemProfile.characters.length > 1 && (
                          <div className="char-selector-tabs" style={{
                            display: 'flex',
                            gap: '12px',
                            marginBottom: '20px',
                            overflowX: 'auto',
                            paddingBottom: '8px',
                            scrollbarWidth: 'thin'
                          }}>
                            {fivemProfile.characters.map((char, index) => {
                              const isSelected = index === selectedCharIndex;
                              return (
                                <button
                                  key={index}
                                  onClick={() => setSelectedCharIndex(index)}
                                  style={{
                                    flex: '1 0 auto',
                                    background: isSelected ? 'rgba(28, 167, 236, 0.15)' : 'rgba(255, 255, 255, 0.02)',
                                    border: isSelected ? '1px solid #1CA7EC' : '1px solid rgba(255, 255, 255, 0.06)',
                                    borderRadius: '12px',
                                    padding: '10px 16px',
                                    color: isSelected ? '#fff' : '#aaa',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    boxShadow: isSelected ? '0 4px 15px rgba(28, 167, 236, 0.2)' : 'none',
                                    outline: 'none',
                                    backdropFilter: 'blur(4px)',
                                    WebkitBackdropFilter: 'blur(4px)'
                                  }}
                                >
                                  <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: isSelected ? '#1CA7EC' : '#fff' }}>
                                    {char.charinfo?.firstname || ''} {char.charinfo?.lastname || ''}
                                  </div>
                                  <div style={{ fontSize: '0.7rem', color: isSelected ? 'rgba(255, 255, 255, 0.8)' : '#888', marginTop: '2px', fontFamily: 'monospace' }}>
                                    ID: {char.citizenid}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {/* Subpestañas del Personaje */}
                        <div className="char-subtabs">
                          <button
                            className={`subtab-btn ${charSubTab === 'perfil' ? 'active' : ''}`}
                            onClick={() => setCharSubTab('perfil')}
                          >
                            <FaUser /> <span>Perfil e Identidad</span>
                          </button>
                          <button
                            className={`subtab-btn ${charSubTab === 'inventario' ? 'active' : ''}`}
                            onClick={() => setCharSubTab('inventario')}
                          >
                            <FaBoxOpen /> <span>Inventario de Bolsillo</span>
                          </button>
                          <button
                            className={`subtab-btn ${charSubTab === 'banco' ? 'active' : ''}`}
                            onClick={() => setCharSubTab('banco')}
                          >
                            <FaExchangeAlt /> <span>Finanzas y Banco</span>
                          </button>
                        </div>

                        {charSubTab === 'perfil' && (
                          <div className="subtab-pane-content">
                            <div className="char-profile-layout">

                              {/* 1. Panel de Identidad Personal */}
                              <div className="char-status-card identity-status-card">
                                <div className="char-card-header blue">
                                  <div className="char-card-icon"><FaUser /></div>
                                  <div>
                                    <h4>Identidad</h4>
                                    <span>Datos Personales</span>
                                  </div>
                                </div>
                                <div className="char-card-body">
                                  <div className="char-body-row">
                                    <span className="char-row-label">Nombre Completo</span>
                                    <span className="char-row-val highlight">{activeChar.charinfo?.firstname || ''} {activeChar.charinfo?.lastname || ''}</span>
                                  </div>
                                  <div className="char-body-row border-top">
                                    <span className="char-row-label">CitizenID</span>
                                    <span className="char-row-val gold">{activeChar.citizenid}</span>
                                  </div>
                                  <div className="char-body-row border-top">
                                    <span className="char-row-label">Teléfono</span>
                                    <span className="char-row-val">{activeChar.charinfo?.phone || 'S/D'}</span>
                                  </div>
                                </div>
                              </div>

                              {/* 2. Panel de Carrera / Trabajo */}
                              <div className="char-status-card job-status-card">
                                <div className="char-card-header green">
                                  <div className="char-card-icon"><FaShieldAlt /></div>
                                  <div>
                                    <h4>Carrera Profesional</h4>
                                    <span>Ocupación y Rango</span>
                                  </div>
                                </div>
                                <div className="char-card-body">
                                  <div className="char-body-row">
                                    <span className="char-row-label">Trabajo Actual</span>
                                    <span className="char-row-val highlight emerald">{activeChar.job?.label || 'Desempleado'}</span>
                                  </div>
                                  <div className="char-body-row border-top">
                                    <span className="char-row-label">Rango</span>
                                    <span className="char-row-val">{activeChar.job?.grade?.name || 'Recluta'}</span>
                                  </div>
                                  <div className="char-body-row border-top">
                                    <span className="char-row-label">Nivel de Rango</span>
                                    <span className="char-row-val blue-text">Grado {activeChar.job?.grade?.level || 0}</span>
                                  </div>
                                </div>
                              </div>

                              {/* 3. Panel de Estado Físico */}
                              <div className="char-status-card physical-status-card">
                                <div className="char-card-header orange">
                                  <div className="char-card-icon"><FaHeartbeat /></div>
                                  <div>
                                    <h4>Estado Físico</h4>
                                    <span>Signos Vitales</span>
                                  </div>
                                </div>
                                <div className="char-card-body">
                                  <div className="vital-stats-grid" style={{ marginTop: 0, paddingTop: 0 }}>
                                    <div className="vital-stat-bar">
                                      <div className="vital-label">
                                        <span>Salud</span>
                                        <span>{Math.round(activeChar.metadata?.health ?? 100)}%</span>
                                      </div>
                                      <div className="vital-progress-bg">
                                        <div className="vital-progress-fill health" style={{ width: `${activeChar.metadata?.health ?? 100}%` }}></div>
                                      </div>
                                    </div>
                                    <div className="vital-stat-bar">
                                      <div className="vital-label">
                                        <span>Blindaje</span>
                                        <span>{Math.round(activeChar.metadata?.armor ?? 0)}%</span>
                                      </div>
                                      <div className="vital-progress-bg">
                                        <div className="vital-progress-fill armor" style={{ width: `${activeChar.metadata?.armor ?? 0}%` }}></div>
                                      </div>
                                    </div>
                                    <div className="vital-stat-bar">
                                      <div className="vital-label">
                                        <span>Hambre</span>
                                        <span>{Math.round(activeChar.metadata?.hunger ?? 100)}%</span>
                                      </div>
                                      <div className="vital-progress-bg">
                                        <div className="vital-progress-fill hunger" style={{ width: `${activeChar.metadata?.hunger ?? 100}%` }}></div>
                                      </div>
                                    </div>
                                    <div className="vital-stat-bar">
                                      <div className="vital-label">
                                        <span>Sed</span>
                                        <span>{Math.round(activeChar.metadata?.thirst ?? 100)}%</span>
                                      </div>
                                      <div className="vital-progress-bg">
                                        <div className="vital-progress-fill thirst" style={{ width: `${activeChar.metadata?.thirst ?? 100}%` }}></div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* 4. Panel de Licencias y Permisos Civiles */}
                              <div className="char-status-card license-status-card">
                                <div className="char-card-header yellow">
                                  <div className="char-card-icon"><FaIdCard /></div>
                                  <div>
                                    <h4>Licencias Civiles</h4>
                                    <span>Permisos y Acreditaciones</span>
                                  </div>
                                </div>
                                <div className="char-card-body">
                                  <div className="license-badge-grid">
                                    <div className={`license-card-badge ${activeChar.metadata?.licences?.driver ? 'active' : 'inactive'}`}>
                                      <div className="license-card-header">
                                        <span className="license-type">A-1</span>
                                        <span className="license-status-icon">{activeChar.metadata?.licences?.driver ? '✓' : '✗'}</span>
                                      </div>
                                      <div className="license-card-body">
                                        <span className="license-card-title">Permiso Conducir</span>
                                        <span className="license-card-desc">{activeChar.metadata?.licences?.driver ? 'Habilitado' : 'No Adquirido'}</span>
                                      </div>
                                    </div>

                                    <div className={`license-card-badge ${activeChar.metadata?.licences?.weapon ? 'active' : 'inactive'}`}>
                                      <div className="license-card-header">
                                        <span className="license-type">W-L</span>
                                        <span className="license-status-icon">{activeChar.metadata?.licences?.weapon ? '✓' : '✗'}</span>
                                      </div>
                                      <div className="license-card-body">
                                        <span className="license-card-title">Porte de Armas</span>
                                        <span className="license-card-desc">{activeChar.metadata?.licences?.weapon ? 'Habilitado' : 'No Adquirido'}</span>
                                      </div>
                                    </div>

                                    <div className={`license-card-badge ${activeChar.metadata?.licences?.business ? 'active' : 'inactive'}`}>
                                      <div className="license-card-header">
                                        <span className="license-type">B-Z</span>
                                        <span className="license-status-icon">{activeChar.metadata?.licences?.business ? '✓' : '✗'}</span>
                                      </div>
                                      <div className="license-card-body">
                                        <span className="license-card-title">Comercial</span>
                                        <span className="license-card-desc">{activeChar.metadata?.licences?.business ? 'Habilitado' : 'No Adquirido'}</span>
                                      </div>
                                    </div>

                                    <div className={`license-card-badge ${activeChar.metadata?.licences?.pilot ? 'active' : 'inactive'}`}>
                                      <div className="license-card-header">
                                        <span className="license-type">PL-1</span>
                                        <span className="license-status-icon">{activeChar.metadata?.licences?.pilot ? '✓' : '✗'}</span>
                                      </div>
                                      <div className="license-card-body">
                                        <span className="license-card-title">Vuelo / Piloto</span>
                                        <span className="license-card-desc">{activeChar.metadata?.licences?.pilot ? 'Habilitado' : 'No Adquirido'}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* 5. Panel de Estadísticas Generales */}
                              <div className="char-status-card stats-status-card">
                                <div className="char-card-header purple">
                                  <div className="char-card-icon"><FaChartLine /></div>
                                  <div>
                                    <h4>Estadísticas de Juego</h4>
                                    <span>Actividad del Personaje</span>
                                  </div>
                                </div>
                                <div className="char-card-body">

                                  {/* Organización / Banda */}
                                  <div className="char-body-row">
                                    <span className="char-row-label">Organización</span>
                                    {activeChar.stats?.has_gang ? (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                                        <span className="char-row-val highlight gold">
                                          {activeChar.stats.gang_name}
                                        </span>
                                        {activeChar.stats.gang_is_boss && (
                                          <span style={{ fontSize: '0.6rem', background: 'rgba(255,215,0,0.15)', color: '#ffd700', padding: '1px 5px', borderRadius: '4px', fontWeight: '700', border: '1px solid rgba(255,215,0,0.3)' }}>BOSS</span>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="char-row-val" style={{ color: '#555', fontStyle: 'italic' }}>Sin Afiliación</span>
                                    )}
                                    {activeChar.stats?.gang_grade && activeChar.stats.has_gang && (
                                      <span style={{ fontSize: '0.7rem', color: '#888', marginTop: '1px' }}>{activeChar.stats.gang_grade}</span>
                                    )}
                                  </div>

                                  {/* Última Conexión */}
                                  <div className="char-body-row border-top">
                                    <span className="char-row-label">Última Sesión</span>
                                    <span className="char-row-val">{activeChar.stats?.last_seen || 'Desconocida'}</span>
                                  </div>

                                  {/* KPIs compactos: Vehículos, Transacciones */}
                                  <div className="char-body-row border-top" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '2px' }}>
                                    <div style={{ background: 'rgba(28,167,236,0.06)', borderRadius: '8px', padding: '8px 10px', border: '1px solid rgba(28,167,236,0.1)', textAlign: 'center' }}>
                                      <div style={{ fontSize: '1.3rem', fontWeight: '800', color: '#1ca7ec', fontFamily: 'monospace' }}>
                                        {activeChar.stats?.total_vehicles ?? activeChar.vehicles?.length ?? 0}
                                      </div>
                                      <div style={{ fontSize: '0.6rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>Vehículos</div>
                                    </div>
                                    <div style={{ background: 'rgba(0,230,118,0.06)', borderRadius: '8px', padding: '8px 10px', border: '1px solid rgba(0,230,118,0.1)', textAlign: 'center' }}>
                                      <div style={{ fontSize: '1.3rem', fontWeight: '800', color: '#00e676', fontFamily: 'monospace' }}>
                                        {activeChar.stats?.num_transactions ?? activeChar.transactions?.length ?? 0}
                                      </div>
                                      <div style={{ fontSize: '0.6rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>Transacciones</div>
                                    </div>
                                  </div>

                                  {/* Dinero Negro */}
                                  {(activeChar.stats?.dirty_money > 0) && (
                                    <div className="char-body-row border-top">
                                      <span className="char-row-label" style={{ color: 'rgba(255,70,70,0.7)' }}>⚠ Dinero No Declarado</span>
                                      <span style={{ fontSize: '0.92rem', color: '#ff4444', fontWeight: '700', fontFamily: 'monospace' }}>
                                        ${activeChar.stats.dirty_money.toLocaleString()}
                                      </span>
                                    </div>
                                  )}

                                </div>
                              </div>
                            </div>

                            {/* 4. Panel de Vehículos Minimalista */}
                            <div style={{
                              background: 'rgba(15, 18, 25, 0.25)',
                              border: '1px solid rgba(255, 255, 255, 0.05)',
                              borderRadius: '16px',
                              padding: '20px',
                              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                              marginTop: '35px'
                            }}>
                              <h4 style={{ color: '#fff', marginBottom: '18px', fontSize: '0.95rem', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '10px', fontWeight: '700', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <FaCar style={{ color: '#1ca7ec' }} /> Mis Vehículos Registrados
                                </span>
                                <span style={{ fontSize: '0.75rem', color: '#aaa', background: 'rgba(255,255,255,0.03)', padding: '2px 8px', borderRadius: '4px' }}>
                                  Total: {activeChar.vehicles ? activeChar.vehicles.length : 0}
                                </span>
                              </h4>

                              {activeChar.vehicles && activeChar.vehicles.length > 0 ? (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '15px' }}>
                                  {activeChar.vehicles.map((veh, index) => {
                                    let stateLabel = 'Guardado';
                                    let stateColor = '#00e676';
                                    let stateBg = 'rgba(0, 230, 118, 0.05)';
                                    if (veh.state === 1) {
                                      stateLabel = 'Fuera';
                                      stateColor = '#ff6b6b';
                                      stateBg = 'rgba(255, 107, 107, 0.05)';
                                    } else if (veh.state === 2) {
                                      stateLabel = 'Depósito';
                                      stateColor = '#ffb300';
                                      stateBg = 'rgba(255, 179, 0, 0.05)';
                                    }

                                    // Calcular porcentajes de salud y combustible
                                    const fuelVal = veh.fuel !== undefined ? Math.round(veh.fuel) : 100;
                                    const engineVal = veh.engine !== undefined ? Math.max(0, Math.min(100, Math.round(veh.engine / 10))) : 100;
                                    const bodyVal = veh.body !== undefined ? Math.max(0, Math.min(100, Math.round(veh.body / 10))) : 100;
                                    const isWanted = veh.wanted === 1 || veh.wanted === true;

                                    // Descodificar mods
                                    let upgradeBadges = [];
                                    try {
                                      if (veh.mods) {
                                        const mods = typeof veh.mods === 'string' ? JSON.parse(veh.mods) : veh.mods;
                                        if (mods.modEngine !== undefined && mods.modEngine !== -1) {
                                          upgradeBadges.push(`Motor Lvl ${mods.modEngine + 1}`);
                                        }
                                        if (mods.modTransmission !== undefined && mods.modTransmission !== -1) {
                                          upgradeBadges.push("Transmisión Pro");
                                        }
                                        if (mods.modBrakes !== undefined && mods.modBrakes !== -1) {
                                          upgradeBadges.push("Frenos Pro");
                                        }
                                        if (mods.modTurbo !== undefined && (mods.modTurbo === 1 || mods.modTurbo === true)) {
                                          upgradeBadges.push("Turbo");
                                        }
                                      }
                                    } catch (err) { }

                                    return (
                                      <div
                                        key={index}
                                        style={{
                                          background: 'rgba(255, 255, 255, 0.02)',
                                          border: '1px solid rgba(255, 255, 255, 0.04)',
                                          borderRadius: '12px',
                                          padding: '16px',
                                          display: 'flex',
                                          flexDirection: 'column',
                                          justifyContent: 'space-between',
                                          minHeight: '180px',
                                          transition: 'all 0.25s ease',
                                          position: 'relative',
                                          overflow: 'hidden'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
                                          e.currentTarget.style.borderColor = 'rgba(28, 167, 236, 0.2)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)';
                                          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.04)';
                                        }}
                                      >
                                        {isWanted && (
                                          <div className="veh-wanted-badge">
                                            BUSCADO
                                          </div>
                                        )}

                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                          <div>
                                            <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#fff', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                              {veh.vehicle}
                                            </div>
                                            <div style={{ fontSize: '0.7rem', color: '#777', marginTop: '2px' }}>
                                              Ubicación: <strong style={{ color: '#aaa' }}>{veh.garage || 'Desconocido'}</strong>
                                            </div>
                                          </div>

                                          <span style={{
                                            fontSize: '0.68rem',
                                            fontWeight: 'bold',
                                            color: stateColor,
                                            backgroundColor: stateBg,
                                            padding: '3px 8px',
                                            borderRadius: '4px',
                                            textTransform: 'uppercase'
                                          }}>
                                            {stateLabel}
                                          </span>
                                        </div>

                                        {/* Indicadores técnicos de salud del vehículo */}
                                        <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                          {/* Barra Combustible */}
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', color: '#888' }}>
                                              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><FaGasPump size={9} /> Combustible</span>
                                              <span style={{ fontWeight: '600', color: '#ccc' }}>{fuelVal}%</span>
                                            </div>
                                            <div style={{ width: '100%', height: '4px', background: 'rgba(255,255,255,0.04)', borderRadius: '2px', overflow: 'hidden' }}>
                                              <div style={{ height: '100%', width: `${fuelVal}%`, background: 'linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%)', borderRadius: '2px' }}></div>
                                            </div>
                                          </div>

                                          {/* Salud de Motor y Chasis en dos columnas */}
                                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                            {/* Motor */}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', color: '#888' }}>
                                                <span>⚙ Motor</span>
                                                <span style={{ fontWeight: '600', color: engineVal > 50 ? '#00e676' : '#ff1744' }}>{engineVal}%</span>
                                              </div>
                                              <div style={{ width: '100%', height: '4px', background: 'rgba(255,255,255,0.04)', borderRadius: '2px', overflow: 'hidden' }}>
                                                <div style={{ height: '100%', width: `${engineVal}%`, background: engineVal > 50 ? 'linear-gradient(90deg, #10b981 0%, #34d399 100%)' : 'linear-gradient(90deg, #ef4444 0%, #f87171 100%)', borderRadius: '2px' }}></div>
                                              </div>
                                            </div>
                                            {/* Carrocería */}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', color: '#888' }}>
                                                <span>🛡 Chasis</span>
                                                <span style={{ fontWeight: '600', color: bodyVal > 50 ? '#3b82f6' : '#ff1744' }}>{bodyVal}%</span>
                                              </div>
                                              <div style={{ width: '100%', height: '4px', background: 'rgba(255,255,255,0.04)', borderRadius: '2px', overflow: 'hidden' }}>
                                                <div style={{ height: '100%', width: `${bodyVal}%`, background: bodyVal > 50 ? 'linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%)' : 'linear-gradient(90deg, #ef4444 0%, #f87171 100%)', borderRadius: '2px' }}></div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Modificaciones de Rendimiento */}
                                        {upgradeBadges.length > 0 && (
                                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '12px' }}>
                                            {upgradeBadges.map((badge, bIdx) => (
                                              <span key={bIdx} className="vehicle-mod-tag">
                                                {badge}
                                              </span>
                                            ))}
                                          </div>
                                        )}

                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid rgba(255,255,255,0.03)', paddingTop: '8px', marginTop: '12px' }}>
                                          <div style={{ fontSize: '0.75rem', fontFamily: 'monospace', color: '#ffd700', fontWeight: 'bold', letterSpacing: '0.5px' }}>
                                            {veh.plate}
                                          </div>
                                          <span style={{ fontSize: '0.68rem', color: '#444' }}>Matrícula</span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div style={{ textAlign: 'center', padding: '20px 0', color: '#666' }}>
                                  <p style={{ margin: 0, fontSize: '0.85rem' }}>No tienes vehículos registrados a tu nombre.</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {charSubTab === 'inventario' && (
                          <div className="subtab-pane-content">
                            {/* Inventario de Bolsillo */}
                            <div style={{
                              background: 'rgba(15, 18, 25, 0.25)',
                              border: '1px solid rgba(255, 255, 255, 0.05)',
                              borderRadius: '16px',
                              padding: '20px',
                              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                              marginTop: '30px'
                            }}>
                              <h4 style={{ color: '#fff', marginBottom: '18px', fontSize: '0.95rem', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '10px', fontWeight: '700', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <FaBoxOpen style={{ color: '#1ca7ec' }} /> Mi Inventario de Bolsillo
                                </span>
                                <span style={{ fontSize: '0.75rem', color: '#aaa', background: 'rgba(255,255,255,0.03)', padding: '2px 8px', borderRadius: '4px' }}>
                                  Objetos: {activeChar.inventory ? (Array.isArray(activeChar.inventory) ? activeChar.inventory : Object.values(activeChar.inventory)).filter(item => item && item.name).length : 0}
                                </span>
                              </h4>

                              {activeChar.inventory && (Array.isArray(activeChar.inventory) ? activeChar.inventory : Object.values(activeChar.inventory)).filter(item => item && item.name).length > 0 ? (
                                <div className="inventory-container">
                                  {(Array.isArray(activeChar.inventory) ? activeChar.inventory : Object.values(activeChar.inventory))
                                    .filter(item => item && item.name)
                                    .map((item, idx) => {
                                      const formattedName = item.name
                                        .replace(/WEAPON_/i, '🔫 ')
                                        .replace(/_/g, ' ')
                                        .replace(/-/g, ' ')
                                        .toLowerCase()
                                        .replace(/\b\w/g, c => c.toUpperCase());

                                      const nameLower = item.name.toLowerCase();
                                      let categoryClass = 'category-default';
                                      let displayIcon = '📦';

                                      if (item.name.startsWith('WEAPON_')) {
                                        categoryClass = 'category-weapon';
                                        displayIcon = '🔫';
                                      } else if (nameLower === 'money' || nameLower === 'cash' || nameLower === 'blackmoney') {
                                        categoryClass = 'category-money';
                                        displayIcon = '💵';
                                      } else if (nameLower === 'radio' || nameLower === 'phone' || nameLower === 'lockpick' || nameLower === 'gatekey' || nameLower === 'gps') {
                                        categoryClass = 'category-tech';
                                        if (nameLower === 'radio') displayIcon = '📻';
                                        else if (nameLower === 'phone') displayIcon = '📱';
                                        else displayIcon = '🔑';
                                      } else if (nameLower.includes('armor') || nameLower.includes('vest')) {
                                        categoryClass = 'category-armor';
                                        displayIcon = '🛡️';
                                      }

                                      return (
                                        <div key={idx} className={`inventory-slot ${categoryClass}`}>
                                          {/* Slot Index */}
                                          <span className="inventory-slot-index">{item.slot || idx + 1}</span>

                                          {/* Item Icon */}
                                          <div className="inventory-slot-display">
                                            <img
                                              src={`${apiBase.replace('/api', '')}/inventory-images/${item.name}.png`}
                                              alt={formattedName}
                                              onError={(e) => {
                                                e.target.style.display = 'none';
                                                const fallback = e.target.nextSibling;
                                                if (fallback) fallback.style.display = 'block';
                                              }}
                                              style={{
                                                width: '52px',
                                                height: '52px',
                                                objectFit: 'contain',
                                              }}
                                            />
                                            <div style={{ display: 'none' }}>
                                              {displayIcon}
                                            </div>
                                          </div>

                                          {/* Item Info Footer */}
                                          <div className="inventory-slot-info">
                                            <span className="inventory-slot-name" title={formattedName}>
                                              {formattedName}
                                            </span>
                                            <span className="inventory-slot-count">
                                              x{item.count || 1}
                                            </span>
                                          </div>

                                          {/* Tooltip Hover con Metadatos */}
                                          <div className="inventory-tooltip">
                                            <div className="inventory-tooltip-title">{formattedName}</div>
                                            <div className="inventory-tooltip-row">
                                              <span>Item ID:</span>
                                              <span className="inventory-tooltip-val">{item.name}</span>
                                            </div>
                                            <div className="inventory-tooltip-row">
                                              <span>Ranura (slot):</span>
                                              <span className="inventory-tooltip-val">{item.slot || idx + 1}</span>
                                            </div>
                                            {item.metadata && item.metadata.registered && (
                                              <div className="inventory-tooltip-row">
                                                <span>Propietario:</span>
                                                <span className="inventory-tooltip-val">{item.metadata.registered}</span>
                                              </div>
                                            )}
                                            {item.metadata && item.metadata.serial && (
                                              <div className="inventory-tooltip-row">
                                                <span>Nº de Serie:</span>
                                                <span className="inventory-tooltip-val" style={{ fontFamily: 'monospace' }}>
                                                  {item.metadata.serial}
                                                </span>
                                              </div>
                                            )}
                                            {item.metadata && item.metadata.durability !== undefined && (
                                              <div style={{ marginTop: '5px' }}>
                                                <div className="inventory-tooltip-row" style={{ marginBottom: '2px' }}>
                                                  <span>Estado / Durabilidad:</span>
                                                  <span>{Math.round(item.metadata.durability)}%</span>
                                                </div>
                                                <div className="durability-bar-container">
                                                  <div
                                                    className="durability-bar-fill"
                                                    style={{
                                                      width: `${item.metadata.durability}%`,
                                                      backgroundColor: item.metadata.durability > 50 ? '#00e676' : item.metadata.durability > 25 ? '#ffb300' : '#ff1744'
                                                    }}
                                                  ></div>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                </div>
                              ) : (
                                <div style={{ textAlign: 'center', padding: '20px 0', color: '#666' }}>
                                  <p style={{ margin: 0, fontSize: '0.85rem' }}>Tu inventario está vacío.</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {charSubTab === 'banco' && (
                          <div className="subtab-pane-content">
                            <div className="bank-dashboard-layout">

                              {/* Columna Izquierda: Tarjeta y Saldos */}
                              <div className="bank-dashboard-left">
                                {/* Tarjeta Bancaria Virtual Premium */}
                                <div className="rb-bank-card-container">
                                  <div className="rb-bank-card">
                                    <div className="rb-bank-card-glass"></div>
                                    <div className="rb-bank-card-glow"></div>
                                    <div className="rb-bank-card-header">
                                      <div className="rb-bank-logo">
                                        <span className="logo-icon">🏦</span>
                                        <span className="logo-text">Rias Baixas Bank</span>
                                      </div>
                                      <span className="rb-card-type">VISA GOLD</span>
                                    </div>

                                    <div className="rb-bank-card-chip-row">
                                      <div className="rb-bank-card-chip"></div>
                                      <div className="rb-bank-card-contactless">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M5 17a7 7 0 0 1 0-10"></path>
                                          <path d="M9 19a12 12 0 0 1 0-14"></path>
                                          <path d="M13 21a17 17 0 0 1 0-18"></path>
                                        </svg>
                                      </div>
                                    </div>

                                    <div className="rb-bank-card-number">
                                      {activeChar.citizenid ? `${activeChar.citizenid.slice(0, 3)} - ${activeChar.citizenid.slice(3, 8)} - ONLINE` : 'XXXX-XXXX-ONLINE'}
                                    </div>

                                    <div className="rb-bank-card-footer">
                                      <div className="rb-card-holder">
                                        <span className="card-label">PROPIETARIO</span>
                                        <span className="card-val">{activeChar.charinfo?.firstname || ''} {activeChar.charinfo?.lastname || ''}</span>
                                      </div>
                                      <div className="rb-card-balance">
                                        <span className="card-label">SALDO DISPONIBLE</span>
                                        <span className="card-val">${activeChar.money?.bank ? activeChar.money.bank.toLocaleString() : '0'}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Panel de saldos detallado estilo wallet */}
                                <div className="rb-wallet-balances">
                                  <div className="rb-balance-item bank">
                                    <div className="rb-balance-icon"><FaDollarSign /></div>
                                    <div className="rb-balance-details">
                                      <span className="rb-balance-label">Cuenta Corriente</span>
                                      <span className="rb-balance-val">${activeChar.money?.bank ? activeChar.money.bank.toLocaleString() : '0'}</span>
                                    </div>
                                  </div>

                                  <div className="rb-balance-item cash">
                                    <div className="rb-balance-icon"><FaDollarSign /></div>
                                    <div className="rb-balance-details">
                                      <span className="rb-balance-label">Dinero en Efectivo</span>
                                      <span className="rb-balance-val">${activeChar.money?.cash ? activeChar.money.cash.toLocaleString() : '0'}</span>
                                    </div>
                                  </div>

                                  <div className="rb-balance-item total">
                                    <div className="rb-balance-icon"><FaDollarSign /></div>
                                    <div className="rb-balance-details">
                                      <span className="rb-balance-label">Patrimonio Total</span>
                                      <span className="rb-balance-val">${((activeChar.money?.bank || 0) + (activeChar.money?.cash || 0)).toLocaleString()}</span>
                                    </div>
                                  </div>
                                </div>

                                {/* Historial de Transacciones Recientes */}
                                <div className="rb-bank-panel" style={{ marginTop: '20px', width: '100%', maxWidth: '440px', padding: '20px' }}>
                                  <h4 className="rb-bank-panel-title" style={{ fontSize: '0.95rem', marginBottom: '12px' }}>
                                    <FaExchangeAlt /> Transacciones Recientes
                                  </h4>
                                  {activeChar.transactions && activeChar.transactions.length > 0 ? (
                                    <div className="rb-transactions-list">
                                      {activeChar.transactions.map((tx, idx) => {
                                        const isDeposit = tx.statement_type === 'deposit';
                                        const amountFormatted = (isDeposit ? '+ $' : '- $') + tx.amount.toLocaleString();

                                        // Formatear fecha
                                        let dateStr = 'Reciente';
                                        if (tx.date) {
                                          try {
                                            const d = new Date(tx.date);
                                            if (isNaN(d.getTime()) && typeof tx.date === 'number') {
                                              dateStr = new Date(tx.date * 1000).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
                                            } else {
                                              dateStr = d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
                                            }
                                          } catch (e) { }
                                        }

                                        return (
                                          <div key={idx} className="rb-transaction-item">
                                            <div className="tx-left">
                                              <div className={`tx-icon ${tx.statement_type}`}>
                                                {isDeposit ? '↓' : '↑'}
                                              </div>
                                              <div className="tx-info">
                                                <span className="tx-reason">{tx.reason || 'Operación Bancaria'}</span>
                                                <span className="tx-date">{dateStr}</span>
                                              </div>
                                            </div>
                                            <span className={`tx-amount ${tx.statement_type}`}>
                                              {amountFormatted}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <p style={{ fontSize: '0.8rem', color: '#666', textAlign: 'center', margin: '15px 0' }}>No hay transacciones recientes.</p>
                                  )}
                                </div>

                              </div>

                              {/* Columna Derecha: Formulario de Transferencias y Ajustes */}
                              <div className="bank-dashboard-right">
                                <div className="rb-bank-panel" style={{ marginTop: 0 }}>
                                  <h4 className="rb-bank-panel-title">
                                    <FaExchangeAlt /> Transferencia de Fondos
                                  </h4>
                                  <p className="rb-bank-panel-desc">
                                    Envía dinero de forma instantánea a través del sistema de banca en línea seguro de Rias Baixas. Introduce el CitizenID de destino para verificar el titular.
                                  </p>

                                  <form onSubmit={handleBankTransfer} className="rb-bank-transfer-form">
                                    <div className="rb-form-field recipient-field">
                                      <label htmlFor="transfer-recipient">CitizenID Destinatario</label>
                                      <div className="rb-input-wrapper with-icon">
                                        <span className="rb-input-icon"><FaUser /></span>
                                        <input
                                          type="text"
                                          id="transfer-recipient"
                                          value={transferRecipient}
                                          onChange={(e) => setTransferRecipient(e.target.value.toUpperCase())}
                                          placeholder="Ej: BUT15279"
                                          maxLength={8}
                                          required
                                          className={
                                            recipientVerified === true ? 'verified' :
                                              recipientVerified === false ? 'invalid' :
                                                transferRecipient.length === 8 ? 'checking' : ''
                                          }
                                        />
                                        {checkingRecipient && <div className="rb-inline-spinner"></div>}
                                      </div>

                                      {/* Visualización del estado de validación */}
                                      {checkingRecipient && (
                                        <div className="recipient-validation-msg checking">
                                          <span className="validation-spinner"></span>
                                          <span>Buscando en la red bancaria...</span>
                                        </div>
                                      )}
                                      {!checkingRecipient && recipientVerified === true && (
                                        <div className="recipient-validation-msg success">
                                          <span className="validation-icon">✓</span>
                                          <span>Destinatario confirmado: <strong>{transferRecipientName}</strong></span>
                                        </div>
                                      )}
                                      {!checkingRecipient && recipientVerified === false && (
                                        <div className="recipient-validation-msg error">
                                          <span className="validation-icon">✗</span>
                                          <span>El CitizenID ingresado no existe</span>
                                        </div>
                                      )}
                                      {!checkingRecipient && recipientVerified === null && transferRecipient.length > 0 && transferRecipient.length < 8 && (
                                        <div className="recipient-validation-msg info">
                                          <span className="validation-icon">ℹ</span>
                                          <span>Faltan {8 - transferRecipient.length} caracteres para verificar</span>
                                        </div>
                                      )}
                                    </div>

                                    <div className="rb-form-field amount-field">
                                      <label htmlFor="transfer-amount">Monto a Transferir ($)</label>
                                      <div className="rb-input-wrapper with-icon">
                                        <span className="rb-input-icon"><FaDollarSign /></span>
                                        <input
                                          type="number"
                                          id="transfer-amount"
                                          value={transferAmount}
                                          onChange={(e) => setTransferAmount(e.target.value)}
                                          placeholder="Ej: 500"
                                          min="1"
                                          required
                                        />
                                      </div>
                                    </div>

                                    <button
                                      type="submit"
                                      disabled={transferLoading || checkingRecipient || recipientVerified !== true}
                                      className="rb-btn-bank-submit"
                                    >
                                      {transferLoading ? 'Procesando...' : 'Confirmar Envío'}
                                    </button>
                                  </form>

                                  {transferSuccess && (
                                    <p className="rb-bank-feedback success-msg">
                                      ✓ {transferSuccess}
                                    </p>
                                  )}
                                  {transferError && (
                                    <p className="rb-bank-feedback error-msg">
                                      ⚠️ {transferError}
                                    </p>
                                  )}

                                  {/* Sección de seguridad bancaria o límites */}
                                  <div className="rb-bank-security-info">
                                    <div className="security-info-header">
                                      <span className="security-badge">SEGURIDAD SSL</span>
                                      <span className="security-fee">COMISIÓN: $0</span>
                                    </div>
                                    <p>Las transferencias son inmediatas e irreversibles. Verifique bien los datos del beneficiario antes de proceder.</p>
                                  </div>
                                </div>
                              </div>

                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ background: 'rgba(28,167,236,0.05)', padding: '24px 20px', borderRadius: '14px', border: '1px solid rgba(28,167,236,0.15)', textAlign: 'center' }}>
                        <FaShieldAlt style={{ color: '#1ca7ec', fontSize: '2rem', margin: '0 auto 12px', display: 'block' }} />
                        <p style={{ color: '#ccc', fontSize: '0.95rem', margin: '0 0 8px 0', fontWeight: 600 }}>
                          Cuenta de juego no vinculada aún
                        </p>
                        <p style={{ color: '#888', fontSize: '0.85rem', margin: '0 0 14px 0', lineHeight: 1.6 }}>
                          Para ver tus personajes aquí, <strong style={{ color: '#1ca7ec' }}>conéctate al servidor FiveM una sola vez</strong>.<br />
                          Al cargar tu personaje en juego, el sistema vinculará automáticamente tu cuenta de Discord con tu perfil.
                        </p>
                        <code style={{ background: 'rgba(0,0,0,0.3)', padding: '6px 14px', borderRadius: '6px', color: '#1ca7ec', fontSize: '0.85rem', display: 'inline-block' }}>
                          connect {fivemProfile?.serverIp || '127.0.0.1'}:{fivemProfile?.serverPort || '30120'}
                        </code>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* TAB 3: SOLICITUDES / SOPORTE */}
              {activeTab === 'soporte' && (
                <div className="tab-pane-content">
                  <div className="panel-header">
                    <h2 className="panel-title">Solicitudes y Soporte</h2>
                  </div>

                  {/* Caja informativa / Alerta premium */}
                  <div style={{
                    background: 'rgba(255, 145, 0, 0.05)',
                    border: '1px solid rgba(255, 145, 0, 0.2)',
                    borderRadius: '12px',
                    padding: '16px 20px',
                    color: '#ffb300',
                    fontSize: '0.88rem',
                    lineHeight: '1.5',
                    marginBottom: '25px',
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'flex-start'
                  }}>
                    <div style={{ fontSize: '1.25rem', marginTop: '2px' }}><FaShieldAlt /></div>
                    <div>
                      <strong>Nota de seguridad e información:</strong> Cualquier cambio en la Whitelist o correo requiere de validación manual por parte del equipo de Staff. Por favor, sé explícito en tu mensaje.
                    </div>
                  </div>

                  {/* Formulario de Solicitud de Cambios mejorado */}
                  <div className="change-request-card" style={{ background: 'rgba(15,18,25,0.45)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '30px', boxShadow: 'inset 0 0 20px rgba(0,0,0,0.2)' }}>
                    <h4 style={{ color: '#1ca7ec', marginBottom: '10px', fontSize: '1.1rem', fontWeight: '700' }}>Formulario de Solicitud</h4>
                    <p style={{ color: '#aaa', fontSize: '0.88rem', marginBottom: '25px', lineHeight: '1.4' }}>
                      Rellena los siguientes campos detallando las correcciones o cambios de tu cuenta.
                    </p>
                    <form onSubmit={handleChangeRequestSubmit} className="change-request-form">

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '20px', alignItems: 'end' }}>
                        <div className="form-group" style={{ position: 'relative' }} ref={dropdownRef}>
                          <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', color: '#7dd3fc', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Tipo de Solicitud</label>
                          <div
                            onClick={() => setDropdownOpen(!dropdownOpen)}
                            style={{
                              width: '100%',
                              padding: '12px 14px',
                              borderRadius: '10px',
                              border: '1px solid rgba(255,255,255,0.12)',
                              backgroundColor: 'rgba(0,0,0,0.4)',
                              color: '#fff',
                              fontSize: '0.9rem',
                              cursor: 'pointer',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              boxSizing: 'border-box',
                              userSelect: 'none',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            <span>{changeType}</span>
                            <FaChevronDown style={{
                              transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.3s ease',
                              color: '#1ca7ec',
                              fontSize: '0.8rem'
                            }} />
                          </div>

                          {dropdownOpen && (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              left: '0',
                              right: '0',
                              marginTop: '5px',
                              background: 'rgba(15, 18, 25, 0.98)',
                              border: '1px solid rgba(255, 255, 255, 0.12)',
                              borderRadius: '10px',
                              boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
                              zIndex: '10',
                              overflow: 'hidden',
                              animation: 'fadeIn 0.2s ease-out'
                            }}>
                              {['Datos de Whitelist', 'Correo Electrónico', 'Perfil de Discord', 'Otro Motivo'].map((opt) => (
                                <div
                                  key={opt}
                                  onClick={() => {
                                    setChangeType(opt);
                                    setDropdownOpen(false);
                                  }}
                                  style={{
                                    padding: '12px 14px',
                                    color: changeType === opt ? '#1ca7ec' : '#fff',
                                    backgroundColor: changeType === opt ? 'rgba(28, 167, 236, 0.08)' : 'transparent',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    transition: 'all 0.2s ease',
                                    borderBottom: '1px solid rgba(255, 255, 255, 0.03)'
                                  }}
                                  className="custom-dropdown-option"
                                >
                                  {opt}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="form-group">
                          <label htmlFor="change-email" style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', color: '#7dd3fc', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Correo de Contacto</label>
                          <input
                            type="email"
                            id="change-email"
                            value={changeEmail}
                            onChange={(e) => setChangeEmail(e.target.value)}
                            placeholder="tu@email.com (Opcional)"
                            style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.12)', backgroundColor: 'rgba(0,0,0,0.4)', color: '#fff', fontSize: '0.9rem', outline: 'none', transition: 'border-color 0.3s', boxSizing: 'border-box' }}
                          />
                        </div>
                      </div>

                      <div className="form-group" style={{ marginBottom: '25px' }}>
                        <label htmlFor="change-message" style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', color: '#7dd3fc', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Detalles del Cambio Solicitado</label>
                        <textarea
                          id="change-message"
                          value={changeMessage}
                          onChange={(e) => setChangeMessage(e.target.value)}
                          placeholder="Describe con precisión qué información deseas que el Staff de RiasBaixas actualice o cambie..."
                          rows="4"
                          style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.12)', backgroundColor: 'rgba(0,0,0,0.4)', color: '#fff', fontSize: '0.95rem', resize: 'vertical', outline: 'none', transition: 'border-color 0.3s', boxSizing: 'border-box', lineHeight: '1.5' }}
                          required
                        ></textarea>
                      </div>
                      <button type="submit" disabled={submittingChange} className="dashboard-action-btn primary" style={{ cursor: 'pointer', border: 'none', padding: '12px 30px', borderRadius: '10px', fontWeight: '700', letterSpacing: '0.3px' }}>
                        {submittingChange ? 'Enviando...' : 'Enviar Solicitud'}
                      </button>
                    </form>
                    {changeSuccess && <p className="message success-message" style={{ marginTop: '20px', padding: '12px 16px', borderRadius: '8px', background: 'rgba(0,230,118,0.1)', border: '1px solid rgba(0,230,118,0.25)', color: '#00e676', fontSize: '0.9rem' }}>{changeSuccess}</p>}
                    {changeError && <p className="message error-message" style={{ marginTop: '20px', padding: '12px 16px', borderRadius: '8px', background: 'rgba(255,23,68,0.1)', border: '1px solid rgba(255,23,68,0.25)', color: '#ff1744', fontSize: '0.9rem' }}>{changeError}</p>}
                  </div>
                </div>
              )}

              {/* TAB 4: GALERÍA DE FOTOS */}
              {activeTab === 'galeria' && (
                <div className="tab-pane-content gallery-section-wrapper">
                  <div className="panel-header">
                    <h2 className="panel-title">Galería de Fotos</h2>
                  </div>

                  {/* Formulario de Subida de Fotos */}
                  {user ? (
                    <div className="gallery-upload-card">
                      <h3><FaCamera /> Compartir una Foto</h3>
                      <p>Sube imágenes de tus mejores momentos en el servidor. Todos los ciudadanos registrados pueden participar.</p>
                      <form onSubmit={handlePhotoUpload} className="gallery-upload-form">
                        <div className="gallery-inputs-row">
                          <label className="gallery-file-selector">
                            <FaImage style={{ fontSize: '1.8rem', color: '#1ca7ec' }} />
                            <span>Seleccionar Foto</span>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => setUploadFile(e.target.files[0])}
                              style={{ display: 'none' }}
                              required
                            />
                          </label>
                          <textarea
                            value={uploadComment}
                            onChange={(e) => setUploadComment(e.target.value)}
                            placeholder="Escribe un pequeño comentario o pie de foto sobre tu imagen..."
                            rows="4"
                            className="gallery-comment-input"
                            maxLength={300}
                          ></textarea>
                        </div>

                        <div className="gallery-submit-row">
                          {uploadFile && (
                            <span className="gallery-file-preview">
                              ✓ {uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                          )}
                          <button
                            type="submit"
                            disabled={uploading}
                            className="dashboard-action-btn primary"
                            style={{ border: 'none', cursor: 'pointer' }}
                          >
                            {uploading ? 'Subiendo...' : 'Publicar Foto'}
                          </button>
                        </div>
                      </form>
                      {uploadSuccess && <p className="message success-message" style={{ marginTop: '15px', padding: '12px 16px', borderRadius: '8px', background: 'rgba(0,230,118,0.1)', border: '1px solid rgba(0,230,118,0.25)', color: '#00e676', fontSize: '0.88rem' }}>{uploadSuccess}</p>}
                      {uploadError && <p className="message error-message" style={{ marginTop: '15px', padding: '12px 16px', borderRadius: '8px', background: 'rgba(255,23,68,0.1)', border: '1px solid rgba(255,23,68,0.25)', color: '#ff1744', fontSize: '0.88rem' }}>{uploadError}</p>}
                    </div>
                  ) : (
                    <div className="gallery-upload-card" style={{ textAlign: 'center', background: 'rgba(28, 167, 236, 0.03)', borderColor: 'rgba(28, 167, 236, 0.15)' }}>
                      <h3><FaCamera /> Compartir una Foto</h3>
                      <p>Sube imágenes de tus mejores momentos en el servidor. Todos los ciudadanos registrados pueden participar.</p>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px', marginTop: '20px', padding: '10px 0' }}>
                        <p style={{ color: '#aaa', margin: 0, fontSize: '0.92rem' }}>Debes iniciar sesión con tu cuenta de Discord para poder publicar fotos y dejar comentarios.</p>
                        <Link to="/login" className="dashboard-action-btn primary" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                          <FaDiscord /> <span>Iniciar Sesión con Discord</span>
                        </Link>
                      </div>
                    </div>
                  )}

                  {/* Grid de Visualización de la Galería */}
                  {galleryLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                      <div className="modern-spinner"></div>
                    </div>
                  ) : galleryError ? (
                    <div className="error-message" style={{ padding: '15px', borderRadius: '10px' }}>{galleryError}</div>
                  ) : galleryPhotos.length > 0 ? (
                    <div className="gallery-grid">
                      {galleryPhotos.map((photo) => {
                        const isSystemAdmin = user && (user.role === 'superadmin' || (Array.isArray(user.permissions) && user.permissions.includes('ver_dashboard')));
                        return (
                          <div className="gallery-card" key={photo.id}>
                            {isSystemAdmin && (
                              <button
                                onClick={() => handlePhotoDelete(photo.id)}
                                className="gallery-delete-btn"
                                title="Borrar Foto"
                              >
                                <FaTrashAlt />
                              </button>
                            )}
                            <div 
                              className="gallery-image-wrapper"
                              onClick={() => setActivePhoto(photo)}
                              style={{ cursor: 'pointer' }}
                              title="Hacer clic para ver en grande"
                            >
                              <img
                                src={photo.file_path.startsWith('/api') ? `${apiBase.replace('/api', '')}${photo.file_path}` : `${apiBase}${photo.file_path}`}
                                alt={photo.comment || 'Foto de Rias Baixas'}
                                className="gallery-image"
                                onError={(e) => {
                                  e.target.src = 'https://images.unsplash.com/photo-1542751371-adc38448a05e?q=80&w=600';
                                }}
                              />
                            </div>
                            <div className="gallery-card-content">
                              <p className={`gallery-comment ${!photo.comment ? 'empty' : ''}`}>
                                {photo.comment || 'Sin descripción.'}
                              </p>
                              <div className="gallery-card-meta">
                                <span className="gallery-author">Por: {photo.username}</span>
                                <span className="gallery-date">
                                  {new Date(photo.created_at).toLocaleDateString('es-ES', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric'
                                  })}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', padding: '40px', borderRadius: '16px', textAlign: 'center', color: '#666' }}>
                      <FaImage style={{ fontSize: '3rem', color: 'rgba(255,255,255,0.05)', marginBottom: '15px' }} />
                      <p style={{ margin: 0, fontSize: '0.95rem' }}>La galería está vacía. ¡Sé el primero en compartir una foto!</p>
                    </div>
                  )}\n                </div>
              )}

              {/* TAB: VALORACIONES */}
              {activeTab === 'valoraciones' && (
                <div className="tab-pane-content">
                  <div className="panel-header">
                    <h2 className="panel-title">Valoraciones del Servidor</h2>
                  </div>

                  <div className="reviews-layout">

                    {/* Columna izquierda: Resumen + Formulario */}
                    <div className="reviews-sidebar">

                      {/* Puntuación media */}
                      <div className="reviews-score-card">
                        <div className="reviews-score-number">
                          {reviewsStats.average ? reviewsStats.average.toFixed(1) : '—'}
                        </div>
                        <div className="reviews-score-stars">
                          {[1, 2, 3, 4, 5].map(star => (
                            <FaStar
                              key={star}
                              style={{ color: reviewsStats.average && star <= Math.round(reviewsStats.average) ? '#ffd700' : 'rgba(255,255,255,0.12)', fontSize: '1.1rem' }}
                            />
                          ))}
                        </div>
                        <div className="reviews-score-count">
                          {reviewsStats.total} {reviewsStats.total === 1 ? 'valoración' : 'valoraciones'}
                        </div>

                        {/* Distribución de estrellas */}
                        <div className="reviews-distribution">
                          {(reviewsStats.distribution || []).map(({ star, count }) => (
                            <div key={star} className="reviews-dist-row">
                              <span className="reviews-dist-label">{star} <FaStar style={{ fontSize: '0.65rem', color: '#ffd700' }} /></span>
                              <div className="reviews-dist-bar-bg">
                                <div
                                  className="reviews-dist-bar-fill"
                                  style={{ width: reviewsStats.total > 0 ? `${(count / reviewsStats.total) * 100}%` : '0%' }}
                                />
                              </div>
                              <span className="reviews-dist-count">{count}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Formulario de envío */}
                      {user && user.discord_id ? (
                        hasAlreadyReviewed ? (
                          <div className="reviews-already-done">
                            <FaCheckCircle style={{ color: '#00e676', fontSize: '1.8rem', marginBottom: '10px' }} />
                            <p>Ya has enviado tu valoración.</p>
                            <span>Solo se permite una valoración por usuario.</span>
                          </div>
                        ) : (
                          <form className="reviews-form" onSubmit={handleSubmitReview}>
                            <h4>Tu Valoración</h4>

                            {/* Estrellas interactivas */}
                            <div className="reviews-star-selector">
                              {[1, 2, 3, 4, 5].map(star => (
                                <button
                                  key={star}
                                  type="button"
                                  className={`review-star-btn ${(myRatingHover || myRating) >= star ? 'active' : ''}`}
                                  onMouseEnter={() => setMyRatingHover(star)}
                                  onMouseLeave={() => setMyRatingHover(0)}
                                  onClick={() => setMyRating(star)}
                                  aria-label={`${star} estrellas`}
                                >
                                  <FaStar />
                                </button>
                              ))}
                              {myRating > 0 && (
                                <span className="reviews-star-label">
                                  {['', '😞 Muy malo', '😕 Malo', '😐 Regular', '😊 Bueno', '🤩 Excelente'][myRating]}
                                </span>
                              )}
                            </div>

                            <textarea
                              className="reviews-textarea"
                              placeholder="Escribe tu opinión sobre el servidor... (opcional, máx. 500 caracteres)"
                              value={myComment}
                              onChange={e => setMyComment(e.target.value)}
                              maxLength={500}
                              rows={4}
                            />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                              <span style={{ fontSize: '0.72rem', color: '#555' }}>{myComment.length}/500</span>
                            </div>

                            {submitReviewError && (
                              <p style={{ color: '#ff4444', fontSize: '0.83rem', margin: '8px 0 0', padding: '8px 12px', background: 'rgba(255,68,68,0.08)', borderRadius: '6px', border: '1px solid rgba(255,68,68,0.2)' }}>
                                {submitReviewError}
                              </p>
                            )}
                            {submitReviewSuccess && (
                              <p style={{ color: '#00e676', fontSize: '0.83rem', margin: '8px 0 0', padding: '8px 12px', background: 'rgba(0,230,118,0.08)', borderRadius: '6px', border: '1px solid rgba(0,230,118,0.2)' }}>
                                ✓ {submitReviewSuccess}
                              </p>
                            )}

                            <button
                              type="submit"
                              disabled={submitReviewLoading || !myRating}
                              className="reviews-submit-btn"
                            >
                              {submitReviewLoading ? 'Enviando...' : 'Publicar Valoración'}
                            </button>
                          </form>
                        )
                      ) : (
                        <div className="reviews-login-prompt">
                          <FaDiscord style={{ fontSize: '2rem', color: '#5865F2', marginBottom: '10px' }} />
                          <p>Inicia sesión con Discord para dejar tu valoración</p>
                          <Link to="/login" className="dashboard-action-btn primary" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
                            <FaDiscord /> <span>Iniciar Sesión</span>
                          </Link>
                        </div>
                      )}
                    </div>

                    {/* Columna derecha: Lista de valoraciones */}
                    <div className="reviews-list-col">
                      {reviewsLoading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                          <div className="modern-spinner" />
                        </div>
                      ) : reviewsError ? (
                        <div className="error-message">{reviewsError}</div>
                      ) : reviews.length === 0 ? (
                        <div className="reviews-empty">
                          <FaStar style={{ fontSize: '3rem', color: 'rgba(255,215,0,0.1)', marginBottom: '15px' }} />
                          <p>Aún no hay valoraciones. ¡Sé el primero en opinar!</p>
                        </div>
                      ) : (
                        <div className="reviews-cards">
                          {reviews.map(review => {
                            const avatarUrl = review.discord_id && review.avatar
                              ? `https://cdn.discordapp.com/avatars/${review.discord_id}/${review.avatar}.png?size=64`
                              : `https://cdn.discordapp.com/embed/avatars/${parseInt(review.discord_id || '0') % 5}.png`;
                            const dateStr = review.created_at
                              ? new Date(review.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })
                              : '';
                            const isMyReview = user && user.discord_id === review.discord_id;
                            return (
                              <div key={review.id} className={`review-card ${isMyReview ? 'my-review' : ''}`}>
                                <div className="review-card-header">
                                  <img
                                    src={avatarUrl}
                                    alt={review.username}
                                    className="review-avatar"
                                    onError={e => { e.target.src = 'https://cdn.discordapp.com/embed/avatars/0.png'; }}
                                  />
                                  <div className="review-user-info">
                                    <span className="review-username">
                                      {review.username}
                                      {isMyReview && <span className="review-my-badge">Tú</span>}
                                    </span>
                                    <span className="review-date">{dateStr}</span>
                                  </div>
                                  <div className="review-stars">
                                    {[1, 2, 3, 4, 5].map(s => (
                                      <FaStar key={s} style={{ color: s <= review.rating ? '#ffd700' : 'rgba(255,255,255,0.1)', fontSize: '0.8rem' }} />
                                    ))}
                                  </div>
                                </div>
                                {review.comment && (
                                  <p className="review-comment">"{review.comment}"</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                  </div>
                </div>
              )}

            </>
          )}

        </main>
      </div>

      {/* Modal de edición de perfil */}
      {showEditProfile && (
        <div className="profile-edit-overlay" onClick={() => setShowEditProfile(false)}>
          <div className="profile-edit-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header-premium">
              <div className="modal-avatar-preview-container">
                {user?.avatar ? (
                  <img 
                    src={`https://cdn.discordapp.com/avatars/${user.discord_id}/${user.avatar}.png`} 
                    alt="Discord Avatar" 
                    className="modal-avatar-preview" 
                  />
                ) : (
                  <div className="modal-avatar-preview placeholder"><FaUser /></div>
                )}
                <div className="modal-avatar-badge"><FaDiscord /></div>
              </div>
              <div className="modal-icon-title">
                <FaUserEdit className="title-icon" />
                <h3>Editar Perfil</h3>
              </div>
              <p className="modal-subtitle">Modifica la información de tu cuenta web</p>
            </div>
            
            <div className="modal-form-body">
              <div className="form-group-premium">
                <label className="form-label-premium">Nombre de Usuario</label>
                <div className="input-with-icon-wrapper">
                  <FaUser className="input-icon" />
                  <input
                    type="text"
                    value={editUsername}
                    onChange={e => setEditUsername(e.target.value)}
                    placeholder="Tu nombre de usuario"
                    className="premium-input-field"
                  />
                </div>
              </div>

              <div className="form-group-premium">
                <label className="form-label-premium">Correo Electrónico</label>
                <div className="input-with-icon-wrapper">
                  <FaEnvelope className="input-icon" />
                  <input
                    type="email"
                    value={editEmail}
                    onChange={e => setEditEmail(e.target.value)}
                    placeholder="ejemplo@correo.com"
                    className="premium-input-field"
                  />
                </div>
                <small className="form-help-text">Correo de contacto para notificaciones de la cuenta.</small>
              </div>
              
              <div className="form-group-premium readonly">
                <label className="form-label-premium">Discord ID (Vinculado)</label>
                <div className="input-with-icon-wrapper">
                  <FaDiscord className="input-icon" />
                  <input
                    type="text"
                    value={user?.discord_id || user?.id || ''}
                    readOnly
                    className="premium-input-field readonly"
                  />
                </div>
                <small className="form-help-text">Tu Discord ID está vinculado permanentemente a tu cuenta web.</small>
              </div>
            </div>

            {editMsg && (
              <div className={`message-banner-premium ${editMsg.includes('correctamente') ? 'success' : 'error'}`}>
                <span>{editMsg}</span>
              </div>
            )}

            <div className="modal-actions-premium">
              <button
                onClick={() => setShowEditProfile(false)}
                className="modal-btn-premium cancel-btn"
              >
                Cancelar
              </button>
              <button
                disabled={editSaving || !editUsername.trim()}
                onClick={async () => {
                  if (!editUsername.trim()) return;
                  setEditSaving(true);
                  setEditMsg('');
                  try {
                    const res = await fetch(`${apiBase}/auth/profile/update`, {
                      method: 'PUT',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken}`
                      },
                      body: JSON.stringify({ username: editUsername, email: editEmail })
                    });
                    
                    const data = await res.json();
                    
                    if (!res.ok) {
                      throw new Error(data.message || 'Error al actualizar el perfil');
                    }
                    
                    await validateToken();
                    setEditMsg('¡Perfil actualizado correctamente!');
                    setTimeout(() => setShowEditProfile(false), 1200);
                  } catch (e) {
                    setEditMsg(e.message || 'Error al guardar los cambios.');
                  } finally {
                    setEditSaving(false);
                  }
                }}
                className="modal-btn-premium save-btn"
              >
                {editSaving ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showConfirmModal && (
        <div className="bank-confirm-overlay" onClick={() => setShowConfirmModal(false)}>
          <div className="bank-confirm-modal glass-panel" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title-icon">🏦</span>
              <h3>Confirmación de Operación</h3>
            </div>

            <div className="modal-body">
              <p className="modal-intro">Estás a punto de enviar dinero a través de la Red de Rias Baixas Bank de forma instantánea y sin comisiones.</p>

              <div className="modal-transaction-summary">
                <div className="summary-section">
                  <span className="summary-sec-title">ORDENANTE (TÚ)</span>
                  <div className="summary-card-details">
                    <span className="summary-name">{activeChar?.charinfo?.firstname || ''} {activeChar?.charinfo?.lastname || ''}</span>
                    <span className="summary-id">CitizenID: <strong className="gold">{activeChar?.citizenid}</strong></span>
                  </div>
                </div>

                <div className="summary-arrow-row">
                  <div className="summary-arrow">↓</div>
                </div>

                <div className="summary-section">
                  <span className="summary-sec-title">BENEFICIARIO (DESTINATARIO)</span>
                  <div className="summary-card-details beneficiary">
                    <span className="summary-name highlight">{transferRecipientName}</span>
                    <span className="summary-id">CitizenID: <strong className="gold">{transferRecipient}</strong></span>
                  </div>
                </div>

                <div className="summary-section border-top">
                  <div className="summary-row">
                    <span className="summary-label">IMPORTE A ENVIAR</span>
                    <span className="summary-val green-text">${parseInt(transferAmount).toLocaleString()}</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">COMISIÓN DE RED</span>
                    <span className="summary-val font-free">GRATIS ($0)</span>
                  </div>
                  <div className="summary-row total">
                    <span className="summary-label">TOTAL A DESCONTAR</span>
                    <span className="summary-val highlighted-total">${parseInt(transferAmount).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="modal-notice">
                ⚠️ <strong>Aviso importante:</strong> Esta transferencia es instantánea e irreversible. Asegúrate de que los datos del beneficiario coinciden con la persona a la que deseas enviar los fondos.
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="modal-btn cancel"
                disabled={transferLoading}
              >
                Cancelar
              </button>
              <button
                onClick={executeBankTransfer}
                className="modal-btn confirm"
                disabled={transferLoading}
              >
                {transferLoading ? 'Procesando...' : 'Confirmar y Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox / Visualizador de Fotos en Grande */}
      {activePhoto && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out',
            animation: 'fadeIn 0.2s ease-out'
          }}
          onClick={() => setActivePhoto(null)}
        >
          {/* Botón para cerrar */}
          <button
            style={{
              position: 'absolute',
              top: '20px',
              right: '25px',
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: '50%',
              width: '45px',
              height: '45px',
              color: 'white',
              fontSize: '1.5rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s',
              zIndex: 10000
            }}
            onClick={(e) => {
              e.stopPropagation();
              setActivePhoto(null);
            }}
          >
            &times;
          </button>
          
          <div 
            style={{
              maxWidth: '90%',
              maxHeight: '85%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              cursor: 'default'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={activePhoto.file_path.startsWith('/api') ? `${apiBase.replace('/api', '')}${activePhoto.file_path}` : `${apiBase}${activePhoto.file_path}`}
              alt={activePhoto.comment || 'Foto en grande'}
              style={{
                maxWidth: '100%',
                maxHeight: '75vh',
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.8)',
                border: '1px solid rgba(255,255,255,0.1)',
                objectFit: 'contain'
              }}
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1542751371-adc38448a05e?q=80&w=600';
              }}
            />
            {activePhoto.comment && (
              <p style={{
                color: 'white',
                marginTop: '15px',
                fontSize: '1.05rem',
                textAlign: 'center',
                background: 'rgba(0,0,0,0.6)',
                padding: '10px 20px',
                borderRadius: '20px',
                maxWidth: '600px',
                lineHeight: '1.4',
                border: '1px solid rgba(255,255,255,0.05)',
                margin: '15px 0 0 0'
              }}>
                {activePhoto.comment}
              </p>
            )}
            <p style={{
              color: 'rgba(255,255,255,0.5)',
              marginTop: '10px',
              fontSize: '0.85rem',
              margin: '10px 0 0 0'
            }}>
              Compartida por <strong>{activePhoto.username}</strong> el {new Date(activePhoto.created_at).toLocaleDateString('es-ES')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDashboard;
